import { useEffect, useRef } from 'react';

/**
 * @param {string | (() => string | null | Promise<string | null>)} urlOrResolver
 */
export default function useSSE(urlOrResolver, { onMessage, enabled = true }) {
  const eventSourceRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef(null);

  useEffect(() => {
    const connect = async () => {
      if (!enabled) return;

      const url = typeof urlOrResolver === 'function'
        ? await urlOrResolver()
        : urlOrResolver;
      if (!url) return;

      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          retryCountRef.current = 0;
          onMessage?.(data);
        } catch {
          // ignore non-JSON messages (heartbeats)
        }
      };

      es.onerror = () => {
        es.close();
        eventSourceRef.current = null;

        const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30000);
        retryCountRef.current += 1;

        retryTimeoutRef.current = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [urlOrResolver, enabled, onMessage]);
}
